import { Upload } from "@tamagui/lucide-icons";
import { useToastController } from "@tamagui/toast";
import type { DocumentPickerAsset } from "expo-document-picker";
import type { ImagePickerAsset } from "expo-image-picker";
import { useCallback, useState } from "react";
import { XStack, useMedia } from "tamagui";

import { useExtractMedicareCard } from "@/api/generated";
import type { AttachmentType } from "@/api/mutations/conversation";
import { Button, Paragraph, Text, YStack } from "@/components";
import AttachmentsButton, {
  ALLOWED_ATTACHMENT_MIME_TYPES,
} from "@/components/AttachmentsButton";
import ErrorLabel from "@/components/forms/ErrorLabel";
import SkipOrSubmit from "@/conversation/AskUserQuestionsPanel/SkipOrSubmit";
import PhotoUploadSummary from "@/conversation/AssistantMessage/AskUserQuestions/PhotoUploadSummary";
import useFileDropZone from "@/hooks/useFileDropZone";
import useHandleAttachmentsPicked from "@/hooks/useHandleAttachmentsPicked";
import { usePreClinicStepsStore } from "@/store/preClinicSteps";
import { MAX_SINGLE_FILE_SIZE } from "../AssistantMessage/AskUserQuestions/PhotoUploadQuestion";

const WRONG_CARD_MESSAGE =
  "We need your red, white and blue Medicare card. If you're on a Medicare Advantage plan, this program isn't available to you yet.";

export default function MedicareCardPhotoStep({
  onAddManually,
}: {
  onAddManually: () => void;
}) {
  const { setCollected, goToPage } = usePreClinicStepsStore();
  const [attachments, setAttachments] = useState<AttachmentType[]>([]);
  const [showWrongCard, setShowWrongCard] = useState(false);
  const [attachmentCount, setAttachmentCount] = useState(0);
  const [attachmentSize, setAttachmentSize] = useState(0);
  const toast = useToastController();
  const { lg } = useMedia();

  const { mutate: extractMedicareCard, isPending: isExtracting } =
    useExtractMedicareCard();

  const { handleAttachmentsPicked, isUploading } = useHandleAttachmentsPicked({
    setAttachments,
    attachmentCount,
    attachmentSize,
    setAttachmentCount,
    setAttachmentSize,
  });

  const hasPhotos = attachments.length > 0;
  const hasPendingAttachments = attachments.some(
    (a) => !a.attachmentId && !a.error,
  );
  const blocking = hasPendingAttachments || isUploading;
  const uploadedIds = attachments
    .map((a) => a.attachmentId)
    .filter((id): id is string => !!id);

  const onFilesPicked = useCallback(
    (assets: (DocumentPickerAsset | ImagePickerAsset)[], files: File[]) => {
      const validAssets: (DocumentPickerAsset | ImagePickerAsset)[] = [];
      const validFiles: File[] = [];
      const oversized: AttachmentType[] = [];
      let hasUnsupportedType = false;
      files.forEach((file, i) => {
        if (!ALLOWED_ATTACHMENT_MIME_TYPES.has(file.type.toLowerCase())) {
          hasUnsupportedType = true;
        } else if (file.size > MAX_SINGLE_FILE_SIZE) {
          oversized.push({
            localUri: assets[i].uri,
            name: file.name,
            mimeType: file.type,
            fileSize: file.size,
            isImage: file.type.startsWith("image/"),
            error: "File is too large. Max size is 25MB",
          });
        } else {
          validAssets.push(assets[i]);
          validFiles.push(file);
        }
      });
      if (hasUnsupportedType) {
        toast.show(
          "That file type isn't supported. Please upload an image or PDF.",
          { duration: 10000 },
        );
      }
      if (oversized.length > 0) {
        setAttachments((prev) => [...prev, ...oversized]);
      }
      if (validFiles.length > 0) {
        handleAttachmentsPicked(validAssets, validFiles);
      }
    },
    [handleAttachmentsPicked, toast],
  );

  const { ref: dropZoneRef, isDragging } = useFileDropZone({
    enabled: lg,
    onFiles: onFilesPicked,
  });

  const onSubmit = () => {
    if (uploadedIds.length === 0) return;
    extractMedicareCard(
      { data: { attachment_ids: uploadedIds } },
      {
        onSuccess: (medicareCard) => {
          if (!medicareCard.is_medicare_card) {
            setShowWrongCard(true);
            return;
          }
          setShowWrongCard(false);
          setCollected({ medicareCard });
          goToPage("medicare_manual");
        },
        onError: () => {
          toast.show(
            "We couldn't read that card. You can enter your details manually.",
            { duration: 10000 },
          );
          goToPage("medicare_manual");
        },
      },
    );
  };

  const statusText = blocking
    ? "Uploading…"
    : isExtracting
      ? "Reading your card…"
      : `${attachments.length} file${attachments.length === 1 ? "" : "s"} added`;

  return (
    <>
      {showWrongCard && (
        <ErrorLabel
          role="alert"
          paddingHorizontal="$150"
          paddingBottom="$200"
          alignItems="flex-start"
          textProps={{ flex: 1 }}
        >
          {WRONG_CARD_MESSAGE}
        </ErrorLabel>
      )}
      {lg ? (
        <YStack
          ref={dropZoneRef}
          alignItems="center"
          justifyContent="center"
          paddingVertical="$400"
          marginHorizontal="$150"
          borderWidth={1}
          borderStyle="dashed"
          borderColor={isDragging ? "$fillButtonPrimary" : "$strokeNeutral"}
          backgroundColor={isDragging ? "$fillCallout" : "$transparent"}
          borderRadius="$150"
        >
          <Upload size="$300" color="$iconBase" />
          <Paragraph marginTop="$250" color="$textSecondary" textAlign="center">
            Drag &amp; drop a photo of your Medicare card
          </Paragraph>
          <AttachmentsButton
            trigger={
              <Text bold textDecorationLine="underline">
                or browse
              </Text>
            }
            triggerProps={{ padding: 0, marginVertical: 0 }}
            onAttachmentsPicked={onFilesPicked}
            isUploading={isUploading}
          />
        </YStack>
      ) : (
        <AttachmentsButton
          trigger={
            <YStack alignItems="center" gap="$150">
              <Upload size="$300" color="$iconBase" />
              <Paragraph color="$textSecondary">
                Click to browse files
              </Paragraph>
            </YStack>
          }
          triggerProps={{
            marginHorizontal: "$150",
            marginVertical: 0,
            padding: 0,
            paddingVertical: "$400",
            borderWidth: 1,
            borderStyle: "dashed",
            borderColor: "$strokeNeutral",
            borderRadius: "$150",
            justifyContent: "center",
          }}
          onAttachmentsPicked={onFilesPicked}
          isUploading={isUploading}
        />
      )}
      {hasPhotos && (
        <PhotoUploadSummary
          attachments={attachments}
          onRemove={(localUri) => {
            const toRemove = attachments.find((a) => a.localUri === localUri);
            if (toRemove) {
              setAttachmentCount(attachmentCount - 1);
              setAttachmentSize(attachmentSize - (toRemove.fileSize || 0));
            }
            setAttachments((prev) =>
              prev.filter((a) => a.localUri !== localUri),
            );
          }}
        />
      )}
      <XStack
        alignItems="center"
        gap="$200"
        paddingHorizontal="$150"
        marginTop="$250"
        paddingBottom="$150"
      >
        <Paragraph flex={1} size="$xs" color="$textSecondary">
          {statusText}
        </Paragraph>
        <Button mode="secondary" onPress={onAddManually}>
          Enter details manually
        </Button>
        <SkipOrSubmit
          showSkip={false}
          showSubmit
          submitDisabled={uploadedIds.length === 0 || blocking || isExtracting}
          isLast={false}
          onSkip={onAddManually}
          onSubmit={onSubmit}
        />
      </XStack>
    </>
  );
}
